import { csvValue } from "./csvValue.js";

export function groupBy(items, getKey) {
  const map = new Map();
  (items || []).forEach((item) => {
    const key = getKey(item) || "Other";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  });
  return map;
}

export function indexById(items = []) {
  return new Map(items.map((item) => [String(item.id), item]));
}

export function findById(items = [], id) {
  if (id === undefined || id === null) return undefined;
  return items.find((item) => String(item.id) === String(id));
}

// Two employment types, deliberately. "Intern" is the retired word -- stored
// records still carry it, so isJuniorType matches both while only "Junior"
// is ever offered or displayed. The one place this list and this check are
// defined; every screen that needs either imports from here instead of
// re-deriving its own copy (that drift is exactly how four separately
// maintained classifiers happened in the first place).
export const EMPLOYMENT_TYPE_OPTIONS = ["Full-Time", "Junior"];
export const isJuniorType = (v) => /intern|junior/i.test(String(v || ""));

function entityTenantId(entity = {}) {
  return entity.tenant_id ?? entity.tenantId ?? entity.tenant ?? "";
}

function entityWorkspaceId(entity = {}) {
  return entity.workspace_id ?? entity.workspaceId ?? entity.workspace ?? "";
}

export function resolveActiveEmployee(data = {}) {
  const allEmployees = data.employees || [];
  const me = data.me || {};
  const user = me.user || me.account || me || {};
  const activeTenantId = me.activeTenant?.id || me.tenant?.id || me.tenant || "";
  const activeWorkspaceId = me.activeWorkspace?.id || me.workspace?.id || me.workspace || "";
  const seen = new Set();
  const scopedEmployees = ((me.employees || []).length ? me.employees : allEmployees.filter((item) => String(item.user) === String(user.id)))
    .map((item) => findById(allEmployees, item.id) || item)
    .filter((item) => {
      if (!item?.id) return false;
      const key = String(item.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  if (activeWorkspaceId) {
    const workspaceMatch = scopedEmployees.find((item) => String(entityWorkspaceId(item)) === String(activeWorkspaceId) && (!activeTenantId || String(entityTenantId(item)) === String(activeTenantId)));
    if (workspaceMatch) return workspaceMatch;
  }

  if (activeTenantId) {
    const tenantMatch = scopedEmployees.find((item) => String(entityTenantId(item)) === String(activeTenantId));
    if (tenantMatch) return tenantMatch;
  }

  return scopedEmployees[0] || null;
}

export function filterForEmployee(tasks = [], employeeId) {
  if (!employeeId) return tasks;
  return tasks.filter((task) => String(task.owner) === String(employeeId) || String(task.owner_id) === String(employeeId) || String(task.assignee) === String(employeeId));
}

export function projectName(data, id) {
  return findById(data.projects || [], id)?.name || "";
}

export function employeeName(data, id) {
  if (!id) return "-";
  return findById(data.employees || [], id)?.display_name || findById(data.employees || [], id)?.username || "-";
}

// Somebody's CURRENT pay: the most recent record by effective date.
//
// Pay is a dated history — a raise adds a record, it does not replace the old
// one — so "the pay record for this person" is never a single row and picking
// the first match in a list is picking whichever one the server happened to
// send first. That is how a raise that was correctly recorded still showed the
// old salary on screen. Screens asking "what does this person earn" want the
// latest; screens asking "what did this month pay" must ask the server, which
// resolves it against that month rather than guessing here.
export function latestPayProfile(payProfiles = [], employeeId) {
  if (!employeeId) return null;
  const mine = payProfiles.filter((p) => String(p.employee) === String(employeeId));
  if (!mine.length) return null;
  return mine.reduce((newest, candidate) => {
    const a = new Date(candidate.effective_at || 0).getTime();
    const b = new Date(newest.effective_at || 0).getTime();
    // Ties fall back to the higher id — the one recorded later.
    if (a > b) return candidate;
    if (a === b && Number(candidate.id || 0) > Number(newest.id || 0)) return candidate;
    return newest;
  });
}

export function isCompleted(status = "") {
  return ["completed", "complete", "done", "passed", "submitted", "closed"].includes(String(status).toLowerCase());
}

function boundedProgress(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.min(Math.max(number, 0), 100);
}

function progressFromStatus(status = "") {
  const normalized = String(status || "").toLowerCase();
  if (isCompleted(normalized)) return 100;
  if (normalized.includes("review") || normalized.includes("submit")) return 85;
  if (normalized.includes("progress") || normalized.includes("working")) return 60;
  if (normalized.includes("block") || normalized.includes("delay") || normalized.includes("hold")) return 30;
  if (normalized.includes("open") || normalized.includes("assign") || normalized.includes("sent")) return 20;
  return 0;
}

export function progressForTask(task) {
  const explicitProgress = boundedProgress(task?.metadata?.progress ?? task?.progress_percent ?? task?.progress ?? task?.percentage);
  if (explicitProgress !== null) return explicitProgress;
  return progressFromStatus(task?.status);
}

export function findDailyStatus(statuses = [], employeeId, iso) {
  return statuses.find((item) => String(item.employee) === String(employeeId) && String(item.status_date).slice(0, 10) === iso);
}

export function getAttendanceStatus(dailyStatus, leaveRequests, employeeId, dateIso) {
  const daily = findDailyStatus(dailyStatus || [], employeeId, dateIso);
  if (daily) return { type: "present", entry: daily };
  const onLeave = (leaveRequests || []).some((lr) =>
    String(lr.employee) === String(employeeId) &&
    String(lr.status).toLowerCase() === "approved" &&
    dateIso >= String(lr.starts_on).slice(0, 10) &&
    dateIso <= String(lr.ends_on).slice(0, 10),
  );
  if (onLeave) return { type: "leave", entry: null };
  return { type: "absent", entry: null };
}

export function lastDays(count) {
  const today = new Date();
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() - (count - index - 1));
    return { iso: isoDate(date), label: String(date.getDate()) };
  });
}

export function calendarDays(monthDate) {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const last = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
  const days = Array.from({ length: first.getDay() }, () => null);
  for (let day = 1; day <= last.getDate(); day += 1) {
    const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
    days.push({ day, iso: isoDate(date), future: date > new Date() });
  }
  return days;
}

export function isoDate(value) {
  // Use LOCAL Date Parts — NOT ToISOString() Which Returns UTC.
  // For IST (+5:30): New Date(2026,5,15).toISOString() → "2026-06-14T18:30:00Z" → "2026-06-14"
  // That Makes Every Calendar Cell And Leave Comparison Appear One Day Ahead.
  const y = value.getFullYear();
  const mo = String(value.getMonth() + 1).padStart(2, "0");
  const d = String(value.getDate()).padStart(2, "0");
  return `${y}-${mo}-${d}`;
}

// A moment in the recent past reads as "2h ago" / "Yesterday" — quicker to
// scan than "24 Sep, 14:32" and it needs no mental subtraction. Anything
// older (or in the future, or a date with no time in it) keeps the plain date.
export function recentPast(value, date) {
  if (typeof value === "string" && !value.includes("T") && !value.includes(" ")) return null;
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 0) return null;
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000) === 1) return "Yesterday";
  return null;
}

export function formatDate(value) {
  if (!value) return "None";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return recentPast(value, date) || date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return recentPast(value, date) || date.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function toggleSet(source, value) {
  const next = new Set(source);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

export function uniqueOptions(values) {
  return Array.from(new Set((values || []).filter(Boolean).map((value) => String(value))));
}

function toPascalCase(value) {
  return String(value || "")
    .replace(/[_\-]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join("");
}

export function downloadCsv(filename, columns, rows) {
  const headers = (columns || []).map((column) => toPascalCase(column));
  const safeName = filename && filename.endsWith(".csv") ? filename : `${toPascalCase(filename || "Export")}.csv`;
  const csv = [headers, ...rows].map((row) => row.map(csvValue).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = safeName;
  link.click();
  URL.revokeObjectURL(url);
}
